import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

import throttle from 'lodash.throttle';
import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import select from '#element/select/select';
import network_card from '#card/network-card/network-card';
import util from '#core/util';


// Just some random notes!

/* 
get network types
get location center
set zoom (if search by keyword)
map items:
	set current data to map data
	remove map items that don't match network types
	sort map items by distance from center
	remove map items that are outside of bounds
	remove map items that are beyond result_limit
markers:
	add markers that match network types
	remove markers that don't match network types
create results list


search:
- see if any items have a title matching query
	- if there are, add them to an array, sort them alphabetically, and flag each one as being connected to a keyword search
	- these results will be placed at the top of the results list
- run a standard location search

- on move bounds ("idle" event after "bounds_changed" event):
	- if there are keywords, then:
		- do not consider map bounds

- on reset:
	- unset keyword search flag
	
	
So-called logic:

- you can search by location ("Topeka, KS" or "91201")
- you can also search by keyword ("Birdwatching")
- when a search is performed, if any items on the map have titles that match the keywords, include those items in the results
- when a search is performed, if items are within a certain radius of the location searched, include them in the results
- when searching by location, items are sorted by distance from that location
- when searching by keyword, items are sorted alphabetically
- a single input field is used for both location and keyword
- when a search by location is performed, the map reorients to fit results within the search radius
- results that reside outside of the radius but are visible on the map should also be included in the results, sorted by distance
- when manually moving the map (by panning around in its interactive area), this effectively represents searching by the location in the center of the map, and should trigger a new search and a new list of sorted results
- when searching by keyword, also search by location using the same terms
- when panning around the map, a new search is performed, which includes:
	- network types
	- center of the map as the location
	- any keywords present
	
	
search by location:
	get network types
	get location center
	get result ids within radius
	
	
map items:
	set current data to map data
	remove map items that don't match network types
	sort map items by distance from center
	remove map items that are outside of bounds
	remove map items that are beyond result_limit
markers:
	add markers that match network types
	remove markers that don't match network types
create results list
*/


const state = window.teal_state = window.teal_state || {};
state.map = state.map || {};
state.map.init = state.map.init || false;

const load_callbacks = [];

function get_script_data() {
	if (window.fritzScripts && typeof window.fritzScripts === 'object') {
		return window.fritzScripts;
	}

	window.indivisible_scripts = window.indivisible_scripts || {};
	return window.indivisible_scripts;
}


// Map
const map_container_$ = '.map__results__embed-container';
const new_group_url_attr = 'data-new-group-url';
const map_$ = '#search-map';
const map_data_key = 'map_data';
const map_emails_key = 'map_emails';
const map_styles_key = 'map_styles';
const api_key_key = 'google_maps_key';
const incoming_data_key_lat = 'latitude';
const incoming_data_key_lng = 'longitude';
const update_throttle_time = 300;
const bounds_change_delay = 500;
const bounds_verify_delay = 20;
const focus_map_on_contiguous_states = true;
const meters_per_mile = 1609.34;
const default_zoom = 5;
const default_center = {
	lat: focus_map_on_contiguous_states ? 39 : 0,
	lng: focus_map_on_contiguous_states ? -97 : 0,
};
const first_search_delay = 300;
const track_search_delay = 3000;
let is_first_search = true;
let is_map_visible = true;
let $search_map_container = null;
let search_map = null;
let map_data = null;
let map_data_map = null;
let map_emails = {};
let map_data_request = null;
let map_styles = [];
let geocoder = null;
let bounds_changed_listener = null;
let zoom_out_listener = null;
let idle_listener = null;
let last_time = Date.now();
let bounds_timeout = null;
let verify_bounds_timeout = null;
let track_search_timeout = null;
let previous_tracked_keyword = null;

// Markers: keep colors in sync with map.scss
const hide_markers_during_bounds_change = false;
const marker_color_primary_default = '#57C6D7';
const marker_color_primary_hover = '#507794';
const marker_color_primary_selected = '#FEB92E';
const marker_color_secondary_default = '#00213E';
const marker_color_secondary_hover = '#6c9fc2';
const marker_color_secondary_selected = '#00213E';
const marker_color_text = '#00213E';
const marker_shape_bg_c = 'map-marker-bg';
const marker_shape_inset_c = 'map-marker-inset';
const marker_icon_width = 44;
const marker_icon_height = 50;
const cluster_icon_width = 48;
const cluster_icon_height = 48;
let map_markers = [];
let marker_clusterer = null;
let marker_by_result_id = new Map();  // O(1) lookup cache for hover/click events
let previously_selected_marker = null;

// Results
let global_update_id = 0; // Prevents old results from overwriting new ones
const result_limit = 80;
const render_feed_in_twig = false;

// A11y: Pagination to reduce keyboard navigation burden
const results_page_size = 20;
let results_visible_count = results_page_size;
const load_more_button_$ = '.map__load-more';
const load_more_button_c = 'map__load-more';
const result_page_attr = 'data-result-page';
const results_beyond_page_c = 'network-card--beyond-page';
const results_list_container_$ = '.map__results__feed';
const results_list_$ = (render_feed_in_twig)
	? results_list_container_$ + ' .feed__items'
	: results_list_container_$ + ' .feed--map-results';
const result_id_attr = 'data-map-result-id';
const result_id_delineator = ',';
const result_state_attr = 'data-result-state';
const result_status_attr = 'data-result-status';
const network_type_attr = 'data-network-type'
const network_type_delineator = ';';
const distance_attr = 'data-marker-distance';
const status_selected = 'selected';
const status_hovered = 'hovered';
const hidden_attr = 'data-hidden';
const contact_button_c = 'network-card__contact-button';
const contact_button_$ = `.${contact_button_c}`;
const contact_email_attr = 'data-network-email';
const social_anchor_$ = '.network-card__social .social-anchor';
const result_title_c = 'network-card__title';
const contact_modal_id = 'map__contact-modal';
const contact_modal_recipient_container_$ = '.ginput_container_email';
const contact_modal_recipient_field_$ = 'input[type="email"]';
const contact_modal_recipient_display_c = 'map__contact-modal__recipient';
const minimum_result_count = 3;
const tab_switch_delay = 480; // Keep in sync with .map__results__feed transition time
// const results_panel_animation_time = 300;
let $scrolling_container = null;
// let $result_count = null;
let keyword_result_ids = [];

const icon_library = {};
const icon_library_$ = '.map__icon-library';
const icon_$ = '.icon';
const social_platform_names = {
	facebook: 'Facebook',
	bluesky: 'Bluesky',
	instagram: 'Instagram',
	linkedin: 'LinkedIn',
	x: 'X',
	twitter: 'Twitter',
	youtube: 'YouTube',
	threads: 'Threads',
	website: 'Website',
};
const website_key = 'website';

// Form
const form_container_$ = '.map__form-container';
const form_$ = '.map__form';
const form_group_$ = '.map__form .field-group';
const form_group_focused_c = 'field-group--focused';
const state_abbr_attr = 'data-state-abbr';
const network_type_checkbox_$ = '[name="network-type[]"]';
const reset_button_$ = '.map__form__reset .anchor__link';
const form_no_results_c = 'map__form-container--no-results';
const form_has_filters_c = 'map__form-container--has-filters';
let $form_container = null;
let $keyword_field = null;
// let $radius_field = null;
// let $state_field = null;
let previous_keyword = null;
let search_timeout = null;
const key_search_delay = 500;
// A11y: Debounce checkbox filter to prevent UI lag
let filter_timeout = null;
const filter_debounce_delay = 300;

let has_map = false;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('map')) return false;
		
		
		load.content(() => {
			has_map = assign_elements();
			if (!has_map) return false;
			
			load_google_maps();
		});
		load.all(() => {
			has_map = assign_elements();
			if (!has_map) return false;
			
			// window.addEventListener(g.resize_e, throttle(() => {
			// 	close_on_resize();
			// }, 150));
			
			if (state.map.init) {
				actually_init();
			}
			
			load_callbacks.forEach(callback => {
				if (typeof callback === 'function') {
					callback();
				}
			});
		});
	},
	
};

export default module;
module.init();


/**
 * Init map
 */
function init_map() {
	state.map.init = true;
	
	if (has_map) {
		state.map.init = false;
		actually_init();
	}
}


/**
 * Load Google Maps API
 */
function load_google_maps() {
	window.initMap = init_map;
	
	const scripts = get_script_data();
	const api_key = scripts[api_key_key] || scripts.googleMapsKey || '';
	if (!api_key) {
		console.error('Group map: Missing Google Maps API key');
		return;
	}
	const $script = document.createElement('script');
	$script.src = `https://maps.googleapis.com/maps/api/js?v=weekly&key=${api_key}&callback=initMap&libraries=marker,geometry`;
	$script.async = true;
	$script.defer = true;
	document.head.appendChild($script);
}


/**
 * Initialize map
 */
async function actually_init() {
	draw_map();
	// set_map_styles();
	
	await populate_map_data();
	update_map_visibility();
	// allow_updating_results_by_map_bounds();
	assign_result_event_listeners();
	assign_form_event_listeners();
	
	// const is_mobile = layout.isBreakpointMax('b6');
	const is_mobile = window.innerWidth < 1084;
	
	// If keyword field is prepopulated, run search
	const keywords = $keyword_field.value.trim();
	if (keywords.length) {
		find_by_keyword(keywords);
	}
	
	// If there are no keywords, search from the middle of the country
	// else if (is_mobile) {
	else {
		allow_updating_results_by_map_bounds();
		// reset_search();
	}
	
	// Update reset button visibility based on initial form state
	update_reset_button_visibility();
}


/**
 * Assign elements
 * @return {boolean}
 */
function assign_elements() {
	if (has_map) return true;
	
	$search_map_container = document.querySelector(map_container_$);
	if ($search_map_container === null) return false;
	
	$scrolling_container = document.querySelector(results_list_container_$);
	
	$form_container = document.querySelector(form_container_$);
	// $result_count = $form_container.querySelector('.result-count');
	// $state_field = $form_container.querySelector('[name="state"]');
	$keyword_field = $form_container.querySelector('[name="keyword"]');
	// $radius_field = $form_container.querySelector('[name="radius"]');
	
	window.addEventListener(g.resize_e, throttle(update_map_visibility, 150));
	
	document.querySelectorAll(`${icon_library_$} ${icon_$}`).forEach($icon => {
		const icon_name = $icon.getAttribute('data-icon-name');
		if (!icon_name) return false;
		
		icon_library[icon_name.replace(/^social-/, '')] = $icon.outerHTML;
	});
	
	document.addEventListener(g.click_e, e => {
		const $contact_button = e.target.closest(contact_button_$);
		if (!$contact_button) return;
		
		const email_address = $contact_button.getAttribute(contact_email_attr);
		update_contact_form(email_address);
		
		const platform = 'Email';
		track_contact($contact_button, platform);
	});
	
	document.addEventListener(g.click_e, e => {
		const $social_link = e.target.closest(social_anchor_$);
		if (!$social_link) return;
		
		const platform = $social_link.querySelector('.button__text').innerText.trim();
		track_contact($social_link, platform);
	});
	
	// On switching map/list view, pause idle listener to prevent list results
	// from being updated repeatedly as the map changes size
	document.addEventListener(g.click_e, e => {
		const $tab_button = e.target.closest('.map__header__tabs .tab-item__button');
		if (!$tab_button) return;
		
		disallow_updating_results_by_map_bounds();
		setTimeout(() => {
			allow_updating_results_by_map_bounds();
		}, tab_switch_delay);
	});
	
	const $reset_button = document.querySelector(reset_button_$);
	if ($reset_button) {
		$reset_button.addEventListener(g.click_e, e => {
			e.preventDefault();
			reset_search();
			return false;
		});
	}
	
	// A11y: Load More button click handler (event delegation since button is dynamic)
	document.addEventListener(g.click_e, e => {
		const $load_more_button = e.target.closest(`${load_more_button_$}__button`);
		if (!$load_more_button) return;
		
		load_more_results();
	});
	
	return true;
}


/**
 * Assign event listeners to form elements
 */
function assign_form_event_listeners() {
	// $state_field.addEventListener(g.change_e, e => {
	// 	const state_name = e.target.value;
	// 	const option = e.target.options[e.target.selectedIndex];
	// 	const state_abbr = option.getAttribute(state_abbr_attr);
	// 	find_state(state_name, state_abbr);
	// });
	
	// Submit search on Enter
	$keyword_field.addEventListener(g.keydown_e, e => {
		if (e.key !== 'Enter') return;
		
		e.preventDefault();
		const search_term = e.target.value.trim();
		find_by_keyword(search_term);
	});
	
	$keyword_field.addEventListener(g.input_e, e => {
		clearTimeout(search_timeout);
		clearTimeout(track_search_timeout);
		
		const key_name = e.data;
		const search_term = e.target.value.trim();
		
		if (key_name === 'Enter') {
			e.preventDefault();
			find_by_keyword(search_term);
		} else {
			search_timeout = setTimeout(() => {
				if (search_term !== previous_keyword) {
					find_by_keyword(search_term);
					previous_keyword = search_term;
				}
			}, key_search_delay);
		}
		
		// Enforce numbers only in ZIP code field
		// if (
		// 	isNaN(parseInt(e.key))
		// 	&& !is_special_key(e)
		// ) {
		// 	e.preventDefault();
		// 	return false;
		// }
	});
	
	// $radius_field.addEventListener(g.change_e, e => {
	// 	find_by_keyword(previous_keyword);
	// });
	
	// A11y: Debounce checkbox changes to prevent performance lag
	document.querySelectorAll(`${form_$} ${network_type_checkbox_$}`).forEach($checkbox => {
		$checkbox.addEventListener(g.change_e, e => {
			clearTimeout(filter_timeout);
			filter_timeout = setTimeout(() => {
				filter_by_network_type();
			}, filter_debounce_delay);
		});
	});
}


/**
 * Assign event listeners to results list
 */
function assign_result_event_listeners() {
	document.addEventListener(g.mouse_over_e, e => {
		const $target = e.target;
		const $result = (typeof $target.closest === 'function')
			? $target.closest(`[${result_id_attr}]`)
			: null;
		if (!$result) return;
		
		highlight_marker_from_result($result, marker_color_primary_hover, marker_color_secondary_hover);
	});
	
	document.addEventListener(g.mouse_out_e, e => {
		const $target = e.target;
		const $result = (typeof $target.closest === 'function')
			? $target.closest(`[${result_id_attr}]`)
			: null;
		if (!$result) return;
		
		highlight_marker_from_result($result, marker_color_primary_default, marker_color_secondary_default);
	});
	
	// A11y: Highlight marker on keyboard focus for screen reader and keyboard users
	document.addEventListener('focusin', e => {
		const $target = e.target;
		const $result = (typeof $target.closest === 'function')
			? $target.closest(`[${result_id_attr}]`)
			: null;
		if (!$result) return;
		
		highlight_marker_from_result($result, marker_color_primary_hover, marker_color_secondary_hover);
	});
	
	document.addEventListener('focusout', e => {
		const $target = e.target;
		const $result = (typeof $target.closest === 'function')
			? $target.closest(`[${result_id_attr}]`)
			: null;
		if (!$result) return;
		
		highlight_marker_from_result($result, marker_color_primary_default, marker_color_secondary_default);
	});
	
	document.addEventListener(g.click_e, e => {
		const $target = e.target;
		const $result = (typeof $target.closest === 'function')
			? $target.closest(`[${result_id_attr}]`)
			: null;
		if (!$result) return;
		
		select_marker_by_result($result);
	});
}


/**
 * Find results by keyword
 * @param {string} input
 * @param {int} radius
 */
function find_by_keyword(input, radius = 15) {
	if (!input) return false;
	
	previous_keyword = input;
	// const radius = parseInt($radius_field.value.trim());
	
	find_location({
		input: input,
		radius: radius,
	});
	
	highlight_form_group('keyword');
	clear_state();
	
	if (!is_first_search) return false;
	
	// There is an issue where the first search sometimes doesn't set the bounds
	// of the map correctly, and one solution is to simply run the search again
	setTimeout(() => {
		is_first_search = false;
		find_location({
			input: input,
			radius: radius,
		});
	}, first_search_delay);
}


/**
 * Find results by state
 * @param {string} state_name
 * @param {string} state_abbr
 */
function find_state(state_name = null, state_abbr = null) {
	if (!state_name.length && !state_abbr.length) return false;
	
	find_location({
		input: 'State of ' + state_name,
		state_name: state_name,
		state_abbr: state_abbr,
	});
	
	highlight_form_group('state');
	clear_keyword();
}


/**
 * Clear state select field value
 */
function clear_state() {
	// $state_field.value = '';
	// select.reset($state_field);
}


/**
 * Clear keyword field value
 */
function clear_keyword() {
	$keyword_field.value = '';
}


/**
 * Find data with matching names, and sort alphabetically
 * @param {array} data
 * @param {string} input
 * @return {array}
 */
function find_keyword_matches(data, input) {
	const selected_network_types = get_selected_network_types();
	
	return data.filter(map_item => {
		const item_name = map_item.name.trim().toLowerCase();
		return (
			item_name.includes(input.trim().toLowerCase())
			&& matches_network_types(map_item, selected_network_types)
		);
	}).sort((a, b) => {
		return a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase());
	}).map(map_item => {
		return get_result_id_for_map_item(map_item);
	});
}


/**
 * Find location
 * @param {object}
 * - @param {string} location
 * - @param {int} radius
 * - @param {string} state_name
 * - @param {string} state_abbr
 */
async function find_location({input, radius, state_name, state_abbr} = {}) {
	if (!geocoder) return false;
	
	track_search(input);
	update_url();
	update_reset_button_visibility();
	
	let bounds = await get_bounds_for_location(input);
	keyword_result_ids = find_keyword_matches(map_data, input);
	
	if (hide_markers_during_bounds_change) {
		marker_clusterer.setMap(null);
	}
	
	setTimeout(() => {
		let has_geocoded_bounds = true;
		
		// Pause bounds change listener so that results aren't being updated
		// continuously while the map is being reoriented
		disallow_updating_results_by_map_bounds();
		
		// No results at all
		if (!bounds && !keyword_result_ids.length) return false;
		
		// Set bounds to fit keyword results
		if (!bounds && keyword_result_ids.length) {
			has_geocoded_bounds = false;
			bounds = extend_bounds_to_fit_result_ids(keyword_result_ids);
			radius = Math.max(get_radius_from_bounds(bounds), 5);
		}
		
		let center = bounds.getCenter();
		const map_data_sorted = sort_map_data(center, map_data);
		
		let location_result_ids = (has_geocoded_bounds)
			? find_matching_results_within_radius(center, radius, map_data_sorted)
			: find_matching_results_within_bounds(bounds, map_data_sorted);
		
		// Combine keyword and location results, removing duplicates
		let all_result_ids = [...new Set([...keyword_result_ids, ...location_result_ids])];
		
		// TODO: I think this is never going to happen, because the location
		// search already sets a minimum number of results
		if (all_result_ids.length < minimum_result_count) {
			const first_result_ids = map_data_sorted.slice(0, minimum_result_count).map(map_item => {
				return get_result_id_for_map_item(map_item);
			});
			all_result_ids = [...new Set([...all_result_ids, ...first_result_ids])];
		}
		
		// Update bounds to fit matching marker positions
		bounds = extend_bounds_to_fit_result_ids(all_result_ids, bounds);
		search_map.fitBounds(bounds);
		
		// Populate results list and reinstate bounds change listener
		setTimeout(() => {
			const additional_result_ids = find_peripherally_visible_markers(all_result_ids);
			all_result_ids = [...new Set([...all_result_ids, ...additional_result_ids])];
			
			// Get data, preserving order of result IDs
			const current_data = all_result_ids.map(result_id => {
				return map_data_map.get(result_id);
			});
			
			populate_results_list(current_data);
			allow_updating_results_by_map_bounds();
			if (hide_markers_during_bounds_change) {
				marker_clusterer.setMap(search_map);
			}
			// verify_bounds(bounds);
		}, bounds_change_delay);
		
		// show_results_panel();
		scroll_to_result(0);
	}, 100);
}


/**
 * Get bounds offset from center by a given distance
 * @param {google.maps.LatLng} center
 * @param {float} distance
 * @return {google.maps.LatLngBounds}
 */
function get_offset_from_center(center, distance) {
	const distanceInMeters = distance * meters_per_mile;
	
	const north = google.maps.geometry.spherical.computeOffset(
		center, distanceInMeters, 0    // 0° = north
	);
	const east = google.maps.geometry.spherical.computeOffset(
		center, distanceInMeters, 90   // 90° = east
	);
	const south = google.maps.geometry.spherical.computeOffset(
		center, distanceInMeters, 180  // 180° = south
	);
	const west = google.maps.geometry.spherical.computeOffset(
		center, distanceInMeters, 270  // 270° = west
	);
	
	const bounds = new google.maps.LatLngBounds();
	bounds.extend(north);
	bounds.extend(east);
	bounds.extend(south);
	bounds.extend(west);
	
	return bounds;
}


/**
 * Extend bounds to fit result IDs
 * @param {array} result_ids
 * @param {google.maps.LatLngBounds} bounds
 * @return {google.maps.LatLngBounds}
 */
function extend_bounds_to_fit_result_ids(result_ids, bounds = null) {
	if (!bounds) {
		bounds = new google.maps.LatLngBounds();
	}
	
	result_ids.forEach(result_id => {
		const map_item = map_data_map.get(result_id);
		if (!map_item) return false;
		
		const lat = map_item[incoming_data_key_lat];
		const lng = map_item[incoming_data_key_lng];
		if (!lat || !lng) return false;
		
		bounds.extend(new google.maps.LatLng(lat, lng));
	});
	return bounds;
	
	// Verify that bounds are correct
	verify_bounds(bounds);
}


/**
 * Promise-based geocoder utility for reuse
 * @param {string} address
 * @return {Promise<google.maps.LatLngBounds|null>}
 */
async function get_bounds_for_location(address) {
	return await new Promise(resolve => {
		geocoder.geocode({ address }, (results, status) => {
			// const viewport = results?.[0]?.geometry?.viewport;
			// resolve(
			// 	status === google.maps.GeocoderStatus.OK
			// 	&& viewport ? viewport : null
			// );
			
			if (status !== google.maps.GeocoderStatus.OK) return resolve(null);
			if (status === google.maps.GeocoderStatus.ZERO_RESULTS) return resolve(null);
			if (
				results == null
				|| !results.length
				|| results[0].geometry == null
				|| results[0].geometry.viewport == null
			) return resolve(null);
			
			resolve(results[0].geometry.viewport);
		});
	});
}


/**
 * Calculate radius from bounds
 * @param {google.maps.LatLngBounds} bounds
 * @param {boolean} in_miles
 * @return {float}
 */
function get_radius_from_bounds(bounds, in_miles = true) {
	const center = bounds.getCenter();
	const northeast = bounds.getNorthEast();
	
	// Calculate distances from center to each edge
	const distance_lat = get_distance(
		new google.maps.LatLng(northeast.lat(), center.lng()),
		center,
		in_miles
	);
	const distance_lng = get_distance(
		new google.maps.LatLng(center.lat(), northeast.lng()),
		center,
		in_miles
	);
	
	return Math.min(distance_lat, distance_lng);
}


/**
 * Verify that map bounds are correct
 * 
 * For certain ZIP codes, the map simply does not center itself correctly on
 * the first try, so we have to double-check it
 * 
 * @param {google.maps.LatLngBounds} bounds
 */
function verify_bounds(bounds) {
	clearTimeout(verify_bounds_timeout);
	
	verify_bounds_timeout = setTimeout(() => {
		const actual_center = search_map.getCenter();
		const center_lat = round_coordinate(bounds.getCenter().lat());
		const center_lng = round_coordinate(bounds.getCenter().lng());
		const actual_center_lat = round_coordinate(actual_center.lat());
		const actual_center_lng = round_coordinate(actual_center.lng());
		
		if (
			actual_center_lat !== center_lat
			|| actual_center_lng !== center_lng
		) {
			// search_map.panTo(center);
			search_map.fitBounds(bounds);
		}
	}, bounds_verify_delay);
}


/**
 * Get selected network types
 * @return {array}
 */
function get_selected_network_types() {
	const selected_types = [];
	const $checkboxes = document.querySelectorAll(network_type_checkbox_$);
	$checkboxes.forEach($checkbox => {
		if ($checkbox.checked) {
			selected_types.push(util.kebab($checkbox.value));
		}
	});
	return selected_types;
}


/**
 * Filter results by network type
 */
function filter_by_network_type() {
	const selected_network_types = get_selected_network_types();
	
	// Determine which markers should be visible
	let visible_markers;
	if (!selected_network_types.length) {
		visible_markers = map_markers;
	} else {
		visible_markers = map_markers.filter(marker => {
			const map_item = map_data_map.get(get_result_id_for_marker(marker));
			return map_item && matches_network_types(map_item, selected_network_types);
		});
	}
	
	// Batch operation: clear once, add matching markers once
	// (Previously added all markers then removed non-matching one-by-one,
	// causing clustering algorithm to recalculate ~80 times)
	marker_clusterer.clearMarkers();
	marker_clusterer.addMarkers(visible_markers);
	
	update_results_from_map_center();
	update_url();
	update_reset_button_visibility();
}


/**
 * Highlight form group
 * @param {string} group_name
 */
function highlight_form_group(group_name) {
	// const groups = {
	// 	keyword: $keyword_field,
	// 	// state: $state_field,
	// };
	// Object.keys(groups).forEach(group_key => {
	// 	if (group_name === group_key) {
	// 		groups[group_key].closest(form_group_$).classList.add(form_group_focused_c);
	// 	} else {
	// 		groups[group_key].closest(form_group_$).classList.remove(form_group_focused_c);
	// 	}
	// });
}


/**
 * Sort map data by distance from center location
 * @param {google.maps.LatLng} center
 * @param {array} this_data
 * @return {array}
 */
function sort_map_data(center, this_data) {
	// Pre-compute distances once (avoids creating LatLng objects during sort comparisons)
	const with_distances = this_data.map(item => ({
		item,
		distance: get_distance(center, new google.maps.LatLng(
			item[incoming_data_key_lat],
			item[incoming_data_key_lng]
		))
	}));
	
	// Sort by pre-computed distances
	with_distances.sort((a, b) => a.distance - b.distance);
	
	return with_distances.map(({ item }) => item);
}


/**
 * Find matching results within bounds
 * @param {google.maps.LatLngBounds} bounds
 * @param {array} sorted_data
 * @return {array}
 */
function find_matching_results_within_bounds(bounds, sorted_data) {
	const selected_network_types = get_selected_network_types();
	
	let result_count = 0;
	let matching_result_ids = [];
	sorted_data.forEach((map_item, index) => {
		if (index >= result_limit) return false;
		if (!matches_network_types(map_item, selected_network_types)) return false;
		
		const position = new google.maps.LatLng(
			map_item[incoming_data_key_lat],
			map_item[incoming_data_key_lng]
		);
		const is_in_bounds = bounds.contains(position);
		
		// const distance = get_distance(center, position);
		// set_result_distance($result, distance);
		
		// Determine if result should actually be displayed
		const is_hidden = (
			!is_in_bounds
			&& index >= minimum_result_count
		);
		if (!is_hidden) {
			matching_result_ids.push(get_result_id_for_map_item(map_item));
			result_count += 1;
		}
		// $result.setAttribute(hidden_attr, is_hidden);
	});
	
	// update_result_count(result_count);
	
	return matching_result_ids;
}


/**
 * Find matching results within radius
 * @param {google.maps.LatLng} center
 * @param {int} radius
 * @param {array} sorted_data
 * @return {array}
 */
function find_matching_results_within_radius(center, radius, sorted_data) {
	const selected_network_types = get_selected_network_types();
	
	let result_count = 0;
	let matching_result_ids = [];
	sorted_data.forEach((map_item, index) => {
		if (index >= result_limit) return false;
		if (!matches_network_types(map_item, selected_network_types)) return false;
		
		const position = new google.maps.LatLng(
			map_item[incoming_data_key_lat],
			map_item[incoming_data_key_lng]
		);
		
		// Determine if result should actually be displayed
		const distance = get_distance(center, position);
		// set_result_distance($result, distance);
		const is_hidden = (
			distance > radius
			&& index >= minimum_result_count
		);
		if (!is_hidden) {
			matching_result_ids.push(get_result_id_for_map_item(map_item));
			result_count += 1;
		}
		// $result.setAttribute(hidden_attr, is_hidden);
	});
	
	// update_result_count(result_count);
	
	return matching_result_ids;
}


/**
 * Does this map item match the selected network types?
 * @param {object} map_item
 * @param {array} selected_network_types
 * @return {boolean}
 */
function matches_network_types(map_item, selected_network_types) {
	if (!selected_network_types.length) return true;
	
	// Use pre-computed kebab types (set in populate_map_data)
	return selected_network_types.some(selected_type => {
		return map_item._kebab_network_types.includes(selected_type);
	});
}


/**
 * Update visibility of results when searching by state
 * @param {string} state_name
 * @param {string} state_abbr
 * @return {array}
 */
function set_visiblity_of_state_results(state_name, state_abbr) {
	const $results_container = document.querySelector(results_list_$);
	const $all_results = document.querySelectorAll(`${results_list_$} [${result_id_attr}]`);
	
	// Divide results into separate groups
	const matching_result_ids = [];
	const $matching_results = [];
	const $non_matching_results = [];
	Array.from($all_results).forEach($result => {
		// set_result_distance($result, '');
		const result_state = $result.getAttribute(result_state_attr);
		const is_hidden = (
			result_state.toLowerCase() !== state_name.toLowerCase()
			&& result_state.toLowerCase() !== state_abbr.toLowerCase()
		);
		$result.setAttribute(hidden_attr, is_hidden);
		if (!is_hidden) {
			matching_result_ids.push($result.getAttribute(result_id_attr));
			$matching_results.push($result);
		} else {
			$non_matching_results.push($result);
		}
	});
	
	let result_count = $matching_results.length;
	
	// Replace results list with sorted results
	const $results_sorted = [...$matching_results, ...$non_matching_results];
	$results_container.innerHTML = '';
	$results_sorted.forEach($el => {
		$results_container.appendChild($el);
	});
	
	// update_result_count(result_count);
	
	return matching_result_ids;
}


/**
 * Get result position from result ID attribute
 * @param {element} $el
 * @return {google.maps.LatLng}
 */
function get_position_from_result_id($el) {
	const [lat, lng] = $el.getAttribute(result_id_attr).split(result_id_delineator);
	return new google.maps.LatLng(lat, lng);
}


/**
 * Set distance display on result
 * @param {element} $result_item
 * @param {mixed} distance
 */
function set_result_distance($result_item, distance) {
	distance = (distance !== false)
		? distance
		: '';
	const $distance_p = $result_item.querySelector('p.distance');
	$distance_p.setAttribute(distance_attr, distance);
	$distance_p.innerHTML = distance + ' miles away';
}


/**
 * Get distance between two points
 * @link https://medium.com/@kadircolak1999/find-markers-in-google-maps-api-circle-fa8dc89a37d4
 * @param {google.maps.LatLng} a
 * @param {google.maps.LatLng} b
 * @param {boolean} in_miles
 * @param {boolean} round_result
 * @return {float}
 */
function get_distance(a, b, in_miles = true, round_result = true) {
	if (!a || !b) return false;
	
	let distance = google.maps.geometry.spherical.computeDistanceBetween(a, b);
	if (in_miles) {
		distance = distance / meters_per_mile;
	}
	if (!round_result) return distance;
	
	return (distance < 10)
		? Math.round(distance * 10) / 10
		: Math.round(distance);
}


/**
 * Set visibility of markers outside of search results, but visible on map
 * @param {array} existing_result_ids
 * @return {array}
 */
function find_peripherally_visible_markers(existing_result_ids) {
	if (!is_map_visible) return [];
	
	const bounds = search_map.getBounds();
	
	// Convert to Set for O(1) lookup instead of O(n) per marker
	const existing_set = new Set(existing_result_ids);
	
	const additional_result_ids = [];
	for (const marker of map_markers) {
		const result_id = get_result_id_for_marker(marker);
		if (existing_set.has(result_id)) continue;
		
		const is_visible = bounds.contains(marker.position);
		if (!is_visible) continue;
		
		additional_result_ids.push(result_id);
	}
	
	return additional_result_ids;
}


/**
 * Update results from map center
 */
function update_results_from_map_center() {
	const selected_network_types = get_selected_network_types();
	
	// const center = search_map.getCenter();
	// let bounds = await get_bounds_for_location(center);
	// let bounds = get_offset_from_center(center, 5);
	const bounds = search_map.getBounds();
	
	const map_data_sorted = sort_map_data(bounds.getCenter(), map_data);
	
	// find results, sorted by center
	const matching_results = map_data_sorted.filter(map_item => {
		if (!matches_network_types(map_item, selected_network_types)) return false;
		
		const position = new google.maps.LatLng(
			map_item[incoming_data_key_lat],
			map_item[incoming_data_key_lng]
		);
		return bounds.contains(position);
	});
	
	populate_results_list(matching_results, true);  // Skip staged render when filtering
	
	// zoom_out_listener = google.maps.event.addListenerOnce(search_map, 'idle', () => {
	// 	if (!matching_results.length) {
	// 		const current_zoom = search_map.getZoom();
	// 		search_map.setZoom(current_zoom - 1);
	// 	}
	// });
}


/**
 * Update count above list of results
 * @param {int} count
 */
function update_result_count(count) {
	// $result_count.innerHTML = count + ' result' + (count !== 1 ? 's' : '');
}


/**
 * Create map element with persistent options
 */
async function draw_map() {
	geocoder = new google.maps.Geocoder();
	const options = {
		
		// This is necessary for google.maps.marker.AdvancedMarkerElement, but
		// causes styles not to be loaded
		mapId: 'search_google_map',
		
		center: default_center,
		
		// TODO: possibly customize amount of detail based on zoom level
		// https://stackoverflow.com/questions/15181679/level-of-detail-tuning-google-maps-api
		zoom: default_zoom,
		minZoom: 4,
		maxZoom: 14,
		restriction: {
			latLngBounds: {
				north: 72,
				south: 15,
				east: -55,
				west: -170,
			},
		},
		
		// UI controls
		mapTypeId: google.maps.MapTypeId.ROADMAP,
		mapTypeControl: false,
		scaleControl: false,
		streetViewControl: false,
		rotateControl: false,
		fullscreenControl: false,
		clickableIcons: false,
		backgroundColor: 'none',
		styles: [],
	};
	
	search_map = new google.maps.Map(document.querySelector(map_$), options);
}


/**
 * Listen for changes in map bounds to update search results
 * @param {boolean} update_immediately
 */
function allow_updating_results_by_map_bounds(update_immediately = false) {
	if (update_immediately) {
		update_results_from_visible_markers();
	}
	
	idle_listener = google.maps.event.addListener(search_map, 'idle', () => {
		update_results_from_map_center();
	});
	
	bounds_changed_listener = google.maps.event.addListener(search_map, 'bounds_changed', () => {
		// throttle(update_results_from_visible_markers, update_throttle_time);
		// clearTimeout(bounds_timeout);
		// bounds_timeout = setTimeout(() => {
		// 	update_results_from_visible_markers();
		// }, bounds_change_delay);
	});
}


/**
 * Stop listening for changes in map bounds
 */
function disallow_updating_results_by_map_bounds() {
	remove_listener(idle_listener);
}


/**
 * Remove map event listener
 * @param {google.maps.MapsEventListener} listener
 */
function remove_listener(listener) {
	if (!listener) return false;
	
	google.maps.event.removeListener(listener);
}


/**
 * Set map styles
 * @link https://mapstyle.withgoogle.com/
 */
function set_map_styles() {
	const scripts = get_script_data();
	const map_styles = scripts[map_styles_key];
	search_map.setOptions({
		styles: map_styles,
	});
}


/**
 * Populate map data from JSON file
 * @return {promise}
 */
async function populate_map_data() {
	if (map_data !== null) {
		return map_data;
	}

	if (map_data_request) {
		return map_data_request;
	}

	map_data_request = (async () => {
		let payload = null;
		try {
			payload = await load_map_payload();
		} catch (error) {
			console.error('Group map: Unable to load map data', error);
		}

		apply_map_payload(payload);
		map_data_request = null;
		return map_data;
	})();

	return map_data_request;
}

async function load_map_payload() {
	const scripts = get_script_data();
	let preloaded_data = scripts[map_data_key];
	let preloaded_emails = scripts[map_emails_key];

	const inline_payload = (scripts.groupMap && scripts.groupMap.payload) ? scripts.groupMap.payload : null;
	if (!preloaded_data && inline_payload && inline_payload.map_data) {
		preloaded_data = inline_payload.map_data;
	}
	if (!preloaded_emails && inline_payload && inline_payload.map_emails) {
		preloaded_emails = inline_payload.map_emails;
	}

	if (preloaded_data && preloaded_emails) {
		return {
			map_data: preloaded_data,
			map_emails: preloaded_emails,
		};
	}

	const endpoint = scripts.groupMap && scripts.groupMap.endpoint;
	if (!endpoint) {
		return null;
	}

	const response = await fetch(endpoint, {
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error(`Request failed: ${response.status}`);
	}

	return response.json();
}

function apply_map_payload(payload) {
	const scripts = get_script_data();
	const inline_payload = (payload && typeof payload === 'object' && Object.keys(payload).length)
		? payload
		: (scripts.groupMap && scripts.groupMap.payload) || {};

	const raw_map_data = inline_payload.map_data || scripts[map_data_key] || [];
	const raw_map_emails = inline_payload.map_emails || scripts[map_emails_key] || {};

	const map_array_source = Array.isArray(raw_map_data)
		? raw_map_data
		: Object.values(raw_map_data || {});

	map_data = map_array_source.slice();
	map_emails = raw_map_emails || {};
	
	// Parse coordinates and set network types
	map_data.forEach(map_item => {
		map_item[incoming_data_key_lat] = + map_item[incoming_data_key_lat];
		map_item[incoming_data_key_lng] = + map_item[incoming_data_key_lng];
		
		const filter_tokens = new Set();
		map_item.network_types = [];
		
		if (map_item.statewide_network_member) {
			map_item.network_types.push('Statewide Network');
			filter_tokens.add('statewide-network');
		}
		if (Array.isArray(map_item.group_categories)) {
			map_item.group_categories.forEach(category => {
				const label = (category && (category.name || category.slug)) || '';
				if (label) {
					map_item.network_types.push(label);
				}
				const slug = category && (category.slug || label);
				const normalized_slug = util.kebab(slug || '');
				if (normalized_slug) {
					filter_tokens.add(normalized_slug);
				}
			});
		}
		
		// Pre-compute kebab versions for faster filtering (avoids 320+ util.kebab calls per filter)
		map_item._kebab_network_types = Array.from(filter_tokens);
		
		// Add standalone URL to socials array as a website entry
		if (map_item.url && map_item.url.trim()) {
			if (!map_item.socials) {
				map_item.socials = [];
			}
			
			const has_website = map_item.socials.some(social => {
				return social.category === website_key;
			});
			if (!has_website) {
				map_item.socials.push({
					category: website_key,
					url: map_item.url.trim(),
				});
			}
		}
	});
	
	// Filter out items without valid coordinates
	map_data = map_data.filter(map_item => {
		const lat = map_item[incoming_data_key_lat];
		const lng = map_item[incoming_data_key_lng];
		return !(isNaN(lat) || isNaN(lng));
	});

	map_data_map = new Map(
		map_data.map(item => [get_result_id_for_map_item(item), item])
	);
	
	// Format result names
	map_data.forEach(map_item => {
		map_item.name = clean_name(map_item.name);
	});

	// Sync legacy globals for pattern fixtures or other consumers
	scripts[map_data_key] = map_data;
	scripts[map_emails_key] = map_emails;
	if (scripts !== window.indivisible_scripts) {
		window.indivisible_scripts = window.indivisible_scripts || {};
		window.indivisible_scripts[map_data_key] = map_data;
		window.indivisible_scripts[map_emails_key] = map_emails;
	}
	
	create_markers();
	
	if (!render_feed_in_twig) {
		// This turned out to be populating a large set of results which were
		// immediately replaced when updating the result list by map bounds
		// populate_results_list(map_data);
	}
}


/**
 * Update visibility of map, for mobile/desktop functionality differences
 */
function update_map_visibility() {
	const styles = window.getComputedStyle($search_map_container);
	const display = styles.getPropertyValue('display');
	is_map_visible = (display !== 'none');
}


/**
 * Find result in list from map marker position
 * Uses combined coordinates as a unique ID to latch onto
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 * @return {element}
 */
function find_result_from_marker_position(marker) {
	const result_id = get_result_id_for_marker(marker);
	const result_$ = `${results_list_$} [${result_id_attr}="${result_id}"]`;
	return document.querySelector(result_$);
}


/**
 * Find info from map marker position
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 * @return {object}
 */
function find_info_from_marker_position(marker) {
	const marker_result_id = get_result_id_for_marker(marker);
	const info = map_data.find(map_item => {
		const item_result_id = get_result_id_for_map_item(map_item);
		return item_result_id === marker_result_id;
	});
	return info;
}


/**
 * Get result ID from unique identifier
 * @param {int|string} id
 * @return {string}
 */
function get_result_id(id) {
	return String(id);
}


/**
 * Get result ID for map data item
 * @param {object} map_item
 * @return {string}
 */
function get_result_id_for_map_item(map_item) {
	return get_result_id(map_item.id);
}


/**
 * Get result ID for marker
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 * @return {string}
 */
function get_result_id_for_marker(marker) {
	return marker.map_item_id;
}


/**
 * Normalize social platform slug for class/icon lookup.
 * @param {string} platform
 * @return {string}
 */
function sanitize_social_platform(platform) {
	if (!platform) return '';
	return platform.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}


/**
 * Normalize platform slug for CSS/icon usage (e.g. Twitter -> X).
 * @param {string} platform
 * @return {string}
 */
function get_social_platform_style_slug(platform) {
	if (!platform) return '';
	if (platform === 'twitter') return 'x';
	return platform;
}


/**
 * Derive a presentable label for social links.
 * @param {object} social
 * @return {string}
 */
function get_social_label(social = {}) {
	const platform = sanitize_social_platform(social.category);
	if (social.label && social.label.trim()) {
		return social.label.trim();
	}
	if (social_platform_names[platform]) {
		return social_platform_names[platform];
	}
	if (!platform) {
		return 'Website';
	}
	return format_label_from_slug(platform);
}


/**
 * Convert slug into title case label.
 * @param {string} slug
 * @return {string}
 */
function format_label_from_slug(slug) {
	if (!slug) return 'Link';
	return slug
		.split(/[-_]/)
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
}


/**
 * Create new markers from map data
 */
function create_markers() {
	map_markers = map_data.map(map_item => {
		const position = new google.maps.LatLng(
			map_item[incoming_data_key_lat],
			map_item[incoming_data_key_lng],
		);
		
		const marker = new google.maps.marker.AdvancedMarkerElement({
			position: position,
			content: create_marker_icon(map_item.id, marker_color_primary_default, marker_color_secondary_default),
			map: null, // Do not add marker to map manually, the clusterer will do it
		});
		
		// Store map item ID for lookups
		marker.map_item_id = get_result_id_for_map_item(map_item);
		
		marker.addEventListener(g.click_e, () => {
			select_result_by_marker(marker);
		});
		marker.addEventListener(g.mouse_enter_e, () => {
			highlight_result_by_marker(marker, marker_color_primary_hover, marker_color_secondary_hover);
		});
		marker.addEventListener(g.mouse_leave_e, () => {
			highlight_result_by_marker(marker, marker_color_primary_default, marker_color_secondary_default);
		});
		
		return marker;
	});
	
	// Build O(1) lookup map for hover/click events
	marker_by_result_id = new Map(
		map_markers.map(marker => [get_result_id_for_marker(marker), marker])
	);
	
	/**
	 * @link https://googlemaps.github.io/v3-utility-library/interfaces/_google_markerclustererplus.clustericonstyle.html
	 * @link https://googlemaps.github.io/v3-utility-library/packages/markerclustererplus/examples/advanced_example.html
	 */
	const cluster_icon = 'data:image/svg+xml;base64,' + window.btoa(`
		<svg width="${cluster_icon_width}" height="${cluster_icon_height}" viewBox="0 0 ${cluster_icon_width} ${cluster_icon_height}" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle fill="${marker_color_secondary_default}" cx="${cluster_icon_width / 2}" cy="${cluster_icon_height / 2}" r="${cluster_icon_width / 2}"/>
			<circle fill="${marker_color_primary_default}" cx="${cluster_icon_width / 2}" cy="${cluster_icon_height / 2}" r="20"/>
		</svg>
	`);
	const cluster_icon_styles = [
		{
			width: cluster_icon_width,
			height: cluster_icon_height,
			url: cluster_icon,
			textSize: 16,
			fontWeight: '600',
			// fontFamily: '"DM Sans", sans-serif',
			textColor: marker_color_text,
		},
	];
	
	marker_clusterer = new MarkerClusterer({
		map: search_map,
		markers: map_markers,
		// gridSize: 80,
		algorithm: new GridAlgorithm({
			gridSize: 60,
			// minimumClusterSize: 2,
		}),
		styles: cluster_icon_styles,
		renderer: { // TODO: fix me
			render: ({ count, position }) => {
				const $cluster_div = document.createElement('div');
				$cluster_div.className = 'custom-cluster';
				$cluster_div.textContent = count;
				return new google.maps.marker.AdvancedMarkerElement({
					position,
					content: $cluster_div,
				});
			},
		},
	});
	
	if (!focus_map_on_contiguous_states) {
		center_map(search_map);
	}
}


/**
 * Highlight selected marker on map, and corresponding result in list
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 */
function select_result_by_marker(marker) {
	const result_id = get_result_id_for_marker(marker);
	const $all_results = document.querySelectorAll(`${results_list_$} [${result_id_attr}]`);
	$all_results.forEach($result => {
		if ($result.getAttribute(result_id_attr) === result_id) {
			// A11y: Reveal result if it's beyond current pagination page
			if ($result.classList.contains(results_beyond_page_c)) {
				reveal_paginated_result($result);
			}
			
			$result.setAttribute(result_status_attr, status_selected + ' ' + status_hovered);
			setTimeout(() => {
				const scroll_position = find_result_scroll_position($result);
				scroll_to_result(scroll_position);
			}, $scrolling_container.getAttribute(g.is_active_attr) === g.true_s ? 10 : 500);
		} else {
			$result.setAttribute(result_status_attr, '');
		}
	});
	
	// Reset color on previously selected marker
	set_marker_color(previously_selected_marker, marker_color_primary_default, marker_color_secondary_default);
	set_marker_color(marker, marker_color_primary_selected, marker_color_secondary_selected);
	previously_selected_marker = marker;
	
	// show_results_panel();
}


/**
 * Highlight selected marker on map, and corresponding result in list
 * @param {element} $result
 */
function select_marker_by_result($result) {
	$result = $result.closest(`[${result_id_attr}]`);
	const result_id = $result.getAttribute(result_id_attr);
	$result.setAttribute(result_status_attr, status_selected);
	
	const $siblings = get_siblings($result);
	$siblings.forEach($sibling => {
		$sibling.setAttribute(result_status_attr, '');
	});
	
	set_marker_color(previously_selected_marker, marker_color_primary_default, marker_color_secondary_default);
	
	const new_selected_marker = marker_by_result_id.get(result_id);  // O(1) lookup
	set_marker_color(new_selected_marker, marker_color_primary_selected, marker_color_secondary_selected);
	previously_selected_marker = new_selected_marker;
}


/**
 * Highlight marker from result
 * @param {element} $result
 * @param {string} color_primary
 * @param {string} color_secondary
 */
function highlight_marker_from_result($result, color_primary, color_secondary) {
	$result = $result.closest(`[${result_id_attr}]`);
	const result_status = $result.getAttribute(result_status_attr);
	if (
		result_status
		&& result_status.includes(status_selected)
	) return false;
	
	const result_id = $result.getAttribute(result_id_attr);
	const selected_marker = marker_by_result_id.get(result_id);  // O(1) lookup
	set_marker_color(selected_marker, color_primary, color_secondary);
}


/**
 * Highlight result from marker
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 * @param {string} color
 */
function highlight_result_by_marker(marker, color_primary, color_secondary) {
	const result_id = get_result_id_for_marker(marker);
	const $all_results = document.querySelectorAll(`${results_list_$} [${result_id_attr}]`);
	const $result = Array.from($all_results).find($result => {
		return $result.getAttribute(result_id_attr) === result_id;
	});
	if (!$result) return false;
	
	const result_status = $result.getAttribute(result_status_attr);
	const is_selected = (
		result_status
		&& result_status.includes(status_selected)
	);
	
	if (color_primary === marker_color_primary_hover) {
		$result.setAttribute(
			result_status_attr,
			(is_selected)
				? status_selected + ' ' + status_hovered
				: status_hovered
		);
	} else {
		$result.setAttribute(
			result_status_attr,
			(is_selected)
				? status_selected
				: ''
		);
	}
	
	set_marker_color(marker,
		is_selected ? marker_color_primary_selected : color_primary,
		is_selected ? marker_color_secondary_selected : color_secondary
	);
}


/**
 * Set marker color
 * @param {google.maps.marker.AdvancedMarkerElement} marker
 * @param {string} color_primary
 * @param {string} color_secondary
 */
function set_marker_color(marker, color_primary, color_secondary) {
	if (!marker) return false;
	
	marker.content.querySelector(`.${marker_shape_bg_c}`).setAttribute('fill', color_secondary);
	marker.content.querySelector(`.${marker_shape_inset_c}`).setAttribute('fill', color_primary);
}


/**
 * Generate SVG for marker icon
 * @param {string} color_primary
 * @param {string} color_secondary
 * @return {string}
 */
function generate_marker_svg(color_primary, color_secondary) {
	return `
		<svg width="${marker_icon_width}" height="${marker_icon_height}" viewBox="0 0 ${marker_icon_width} ${marker_icon_height}" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path class="${marker_shape_bg_c}" d="M 44 22
				C 44 36, 31 50, 22 50
				C 13 50, 0 36, 0 22
				A 22 22, 0, 0 1, 22 0
				A 22 22, 0, 0 1, 44 22
				Z" fill="${color_secondary}" />  
			<circle class="${marker_shape_inset_c}" fill="${color_primary}" cx="${marker_icon_width / 2}" cy="${marker_icon_width / 2}" r="20"/>
			<path class="map-marker-icon" d="M 22 13
				C 26 13, 29 16, 29 20
				C 29 24, 25 29, 22 33
				C 19 29, 15 24, 15 20
				C 15 16, 18 13, 22 13
				M 22 17
				A 3 3, 0, 0 1, 22 23
				A 3 3, 0, 0 1, 22 17
				" stroke="${marker_color_text}" stroke-width="1.5" />
		</svg>
	`;
}


/**
 * Create marker icon
 * @param {int|string} map_item_id
 * @param {string} color_primary
 * @param {string} color_secondary
 * @return {google.maps.Icon}
 */
function create_marker_icon(map_item_id, color_primary, color_secondary) {
	const svg_html = generate_marker_svg(color_primary, color_secondary);
	const result_id = get_result_id(map_item_id);
	
	const $svg_container = document.createElement('div');
	$svg_container.setAttribute(result_id_attr, result_id);
	$svg_container.innerHTML = svg_html;
	return $svg_container;
}


/**
 * Set map position and zoom to fit all markers
 * @param {google.maps.Map} map
 */
function center_map(map) {
	const bounds = new google.maps.LatLngBounds();
	map_data.forEach(map_item => {
		const position = new google.maps.LatLng(
			map_item[incoming_data_key_lat], map_item[incoming_data_key_lng]
		);
		bounds.extend(position);
	});
	
	if (map_data.length === 1) {
		map.setCenter(bounds.getCenter());
		map.setZoom(16);
	} else {
		map.fitBounds(bounds);
	}
}


/**
 * Reset search
 */
function reset_search() {
	$keyword_field.value = '';
	document.querySelectorAll(`${form_$} ${network_type_checkbox_$}`).forEach($checkbox => {
		$checkbox.checked = false;
	});
	keyword_result_ids = [];
	
	search_map.setZoom(default_zoom);
	search_map.setCenter(default_center);
	scroll_to_result(0);
	
	update_url();
	update_reset_button_visibility();
}


/**
 * Update reset button visibility based on active filters
 */
function update_reset_button_visibility() {
	if (!$form_container) return;
	
	const has_keywords = $keyword_field && $keyword_field.value.trim() !== '';
	const selected_network_types = get_selected_network_types();
	const has_network_types = selected_network_types.length > 0;
	
	const has_filters = has_keywords || has_network_types;
	
	$form_container.classList.toggle(form_has_filters_c, has_filters);
}


/**
 * A11y: Load more results for pagination
 * Reveals the next page of results and updates the Load More button
 */
function load_more_results() {
	const $results_container = document.querySelector(results_list_$);
	if (!$results_container) return;
	
	// Increase visible count by page size
	results_visible_count += results_page_size;
	
	// Get all result cards
	const $all_results = $results_container.querySelectorAll(`[${result_id_attr}]`);
	const total_results = $all_results.length;
	
	// Update each result's visibility
	$all_results.forEach(($result, index) => {
		const is_beyond_page = index >= results_visible_count;
		
		if (is_beyond_page) {
			$result.classList.add(results_beyond_page_c);
			$result.setAttribute('inert', '');
		} else {
			$result.classList.remove(results_beyond_page_c);
			$result.removeAttribute('inert');
		}
	});
	
	// Update Load More button visibility and count
	const $load_more = $results_container.querySelector(load_more_button_$);
	if ($load_more) {
		const has_more_results = total_results > results_visible_count;
		const shown_results = Math.min(results_visible_count, total_results);
		
		// Update shown count
		const $shown = $load_more.querySelector(`.${load_more_button_c}__shown`);
		if ($shown) {
			$shown.textContent = shown_results;
		}
		
		if (has_more_results) {
			$load_more.removeAttribute('hidden');
		} else {
			$load_more.setAttribute('hidden', '');
		}
	}
}


/**
 * A11y: Reveal a specific paginated result by expanding pagination to include it
 * Used when clicking a map pin whose result is beyond the current page
 * @param {element} $target_result - The result element to reveal
 */
function reveal_paginated_result($target_result) {
	const $results_container = document.querySelector(results_list_$);
	if (!$results_container) return;
	
	// Get all results to find target's index
	const $all_results = Array.from($results_container.querySelectorAll(`[${result_id_attr}]`));
	const target_index = $all_results.indexOf($target_result);
	if (target_index === -1) return;
	
	// Calculate how many results need to be visible
	const required_count = target_index + 1;
	
	// Expand pagination to include target (round up to next page)
	const new_visible_count = Math.ceil(required_count / results_page_size) * results_page_size;
	if (new_visible_count <= results_visible_count) return;
	
	results_visible_count = new_visible_count;
	
	// Update visibility for all results
	$all_results.forEach(($result, index) => {
		const is_beyond_page = index >= results_visible_count;
		
		if (is_beyond_page) {
			$result.classList.add(results_beyond_page_c);
			$result.setAttribute('inert', '');
		} else {
			$result.classList.remove(results_beyond_page_c);
			$result.removeAttribute('inert');
		}
	});
	
	// Update Load More button
	const $load_more = $results_container.querySelector(load_more_button_$);
	if ($load_more) {
		const total_results = $all_results.length;
		const has_more_results = total_results > results_visible_count;
		const shown_results = Math.min(results_visible_count, total_results);
		
		const $shown = $load_more.querySelector(`.${load_more_button_c}__shown`);
		if ($shown) {
			$shown.textContent = shown_results;
		}
		
		if (has_more_results) {
			$load_more.removeAttribute('hidden');
		} else {
			$load_more.setAttribute('hidden', '');
		}
	}
}


/**
 * Update URL with search parameters
 */
function update_url() {
	const keywords_url_param = 'keywords';
	const network_type_url_param = 'network-type';
	const url = new URL(window.location);
	
	const selected_network_types = get_selected_network_types();
	url.searchParams.delete(network_type_url_param);
	selected_network_types.forEach(type => {
		url.searchParams.append(network_type_url_param, type);
	});
	
	const keywords = $keyword_field.value.trim();
	url.searchParams.delete(keywords_url_param);
	if (keywords) {
		url.searchParams.set(keywords_url_param, keywords);
	}
	
	window.history.replaceState({}, '', url);
}


/**
 * Update text list of results
 * @param {array} this_data
 * @param {boolean} skip_staged_render - Skip two-stage rendering (use when filtering)
 */
function populate_results_list(this_data, skip_staged_render = false) {
	global_update_id += 1;
	const this_update_id = global_update_id;
	
	// A11y: Reset pagination when results change
	results_visible_count = results_page_size;
	
	const $results_container = document.querySelector(results_list_$);
	if (!$results_container) return false;
	
	$form_container.classList.remove(form_no_results_c);
	if (!this_data.length) {
		$form_container.classList.add(form_no_results_c);
	}
	
	// const view_result_site = window.indivisible_scripts['view_result_txt'];
	const list_items = [];
	
	// Convert to Set for O(1) lookup instead of O(n) includes() per item
	const keyword_result_ids_set = new Set(keyword_result_ids);
	
	this_data.forEach((map_item, index) => {
		if (this_update_id !== global_update_id) return false;
		if (index >= result_limit) return false;
		
		const result_id = get_result_id_for_map_item(map_item);
		
		// "active": true,
		// "all_in_for_harris": "FALSE",
		// "city": "Ferndale",
		// "congressional_districts": "MI11",
		// "country": "US",
		// "email": false,
		// "facebook": "https://www.facebook.com/groups/368302126865423/",
		// "id": 3,
		// "interaction_count": 0,
		// "latitude": 42.4586,
		// "longitude": -83.1363,
		// "name": "Indivisible Fighting 9",
		// "newcomer_friendly": true,
		// "primary_contact_method": "Website",
		// "public_description": "This group has been together since 2016 ...",
		// "socials": [
		// 	{
		// 		"category": "facebook",
		// 		"url": "https://www.facebook.com/groups/368302126865423/"
		// 	},
		// 	{
		// 		"category": "twitter",
		// 		"url": "https://twitter.com/MIfighting9"
		// 	}
		// ],
		// "state": "MI",
		// "statewide_network_member": "Statewide Indivisible Michigan",
		// "status": "Public (displayed in map)",
		// "twitter": "https://twitter.com/MIfighting9",
		// "url": "http://www.indivisiblefighting9.com/",
		// "zip": "48220"
		
		let meta_header_html = '';
		let meta_header_array = [];
		if (map_item.city || map_item.state) {
			meta_header_array.push([
				(map_item.city || '').trim(),
				(map_item.state || '').trim(),
			].filter(Boolean).join(', '));
		}
		if (map_item.congressional_districts) {
			meta_header_array.push(`Congressional District ${map_item.congressional_districts}`);
		}
		
		meta_header_array = meta_header_array.filter(Boolean);
		if (meta_header_array.length) {
			meta_header_html = `
				<div class="network-card__meta">
					<div class="meta-list">
						${meta_header_array.map(meta => `
							<p class="meta-list__item">${meta}</p>
						`).join('')}
					</div>
				</div>
			`;
		}
		
		let network_types_html = '';
		if (map_item.network_types.length) {
			network_types_html = `
				<div class="network-card__types">
					<div class="anchor-group :shy-false :text-only-true">
						<menu class="anchor-group__items">
							${map_item.network_types.map(type => `
								<li class="anchor-group__item">
									<span class="anchor :text-only-true :shy-false">
										<span class="anchor__text">${type}</span>
										${icon_library['check-starburst']}
									</span>
								</li>
							`).join('')}
						</menu>
					</div>
				</div>
			`;
		}
		
		let abstract_html = '';
		if (map_item.public_description) {
			abstract_html = `
				<div class="network-card__abstract">
					<div id="network-card__abstract__text" class="network-card__abstract__text" data-content>
						${map_item.public_description}
					</div>
					<button class="btn network-card__abstract-button :version-2 :size-1" aria-expanded="false" aria-controls="network-card__abstract__text">
						<span class="button__text text">Show More</span>
						${icon_library.caret}
					</button>
				</div>
			`;
		}
		
		const email = map_emails[map_item.id] ?? null;
		let email_button_html = '';
		if (
			email
			&& map_item.primary_contact_method
			&& map_item.primary_contact_method.toLowerCase() === 'email'
		) {
			email_button_html = `
				<a class="btn ${contact_button_c} :version-1 :size-1" href="#${contact_modal_id}"
					uk-toggle aria-haspopup="dialog" aria-controls="${contact_modal_id}"
					${contact_email_attr}="${email}"
				>
					<span class="button__text text">Contact</span>
				</a>
			`;
		}
		
		let social_anchors_html = '';
		if (map_item.socials) {
			social_anchors_html = `
				<div class="network-card__social">
					<div class="social-anchor-group" data-pattern="social-anchor-group">
						<div class="social-anchor-group__inner pattern__inner">
							<menu class="social-anchor-group__items">
								${map_item.socials.map(social => {
									const platform_slug = sanitize_social_platform(social.category);
									const platform_class = get_social_platform_style_slug(platform_slug) || 'custom';
									const label = get_social_label(social);
									const icon = (platform_class !== 'custom' && (icon_library[platform_class] || icon_library[`social/${platform_class}`]))
										|| (platform_slug && (icon_library[platform_slug] || icon_library[`social/${platform_slug}`]))
										|| icon_library.website
										|| '';
			
									
									return `
										<li>
											<a class="social-anchor btn :icon-before :version-2 :size-1 :platform-${platform_class} :text-true" href="${social.url}" target="_blank" rel="noopener noreferrer">
												${icon}
												<span class="button__text text">${label}</span>
											</a>
										</li>
									`;
								}).join('')}
							</menu>
						</div>
					</div>
				</div>
			`;
		}
		
		// A11y: Track page number for pagination; hide results beyond current page from keyboard
		const page_number = Math.floor(index / results_page_size) + 1;
		const is_beyond_page = index >= results_visible_count;
		
		const item_html = `
			<article
				class="network-card card ${keyword_result_ids_set.has(result_id) ? 'network-card--keyword-result' : ''} ${is_beyond_page ? results_beyond_page_c : ''}"
				data-network-id="${map_item.id}"
				${result_id_attr}="${result_id}" ${result_state_attr}="${map_item.state}"
				${network_type_attr}="${map_item._kebab_network_types.join(';')}"
				${result_page_attr}="${page_number}"
				${hidden_attr}="false"
				${is_beyond_page ? 'inert' : ''}
			>
				<div class="network-card__inner pattern__inner">
					<div class="network-card__header">
						${meta_header_html}
						<h2 class="${result_title_c} title">${map_item.name}</h2>
						${network_types_html}
					</div>
						
					${abstract_html}
					
					${(email_button_html || social_anchors_html)
						? '<div class="network-card__footer">'
						: ''
					}
						${email_button_html}
						${social_anchors_html}
					${(email_button_html || social_anchors_html)
						? '</div>'
						: ''
					}
				</div>
			</article>
		`;
		
		list_items.push(item_html);
	});
	
	// A11y: Add "Load More" button for pagination
	const total_results = Math.min(this_data.length, result_limit);
	const has_more_results = total_results > results_visible_count;
	const shown_results = Math.min(results_visible_count, total_results);
	const load_more_html = `
		<div class="${load_more_button_c}" ${has_more_results ? '' : 'hidden'}>
			<button class="btn :version-1 :size-1 ${load_more_button_c}__button" type="button">
				<span class="button__text text">Load More</span>
			</button>
			<p class="${load_more_button_c}__count">Showing <span class="${load_more_button_c}__shown">${shown_results}</span> of ${total_results} groups</p>
		</div>
	`;
	list_items.push(load_more_html);
	
	const no_results_title = '0 results found';
	const no_results_description = 'Try broadening your search or adjusting your filters.';
	const few_results_description = `If there’s not a group close enough for you, <a href="${$results_container.closest(`[${new_group_url_attr}]`).getAttribute(new_group_url_attr)}">consider starting one</a>.`;
	
	const not_enough_results_html = `
		<article
			class="network-card card network-card--no-results"
			${hidden_attr}="false"
		>
			<div class="network-card__inner pattern__inner">
				<div class="network-card__header network-card__header--no-results">
					<h3 class="${result_title_c} title">${no_results_title}</h3>
				</div>
	
				<div class="network-card__abstract network-card__abstract--no-results" data-content>
					${no_results_description}
				</div>
	
				<div class="network-card__abstract network-card__abstract--few-results" data-content>
					${few_results_description}
				</div>
			</div>
		</article>
	`;
	list_items.push(not_enough_results_html);
	
	// Add results to DOM
	const populate_results_callback = () => {
		if (this_update_id !== global_update_id) return false;
		
		$results_container.innerHTML = list_items.join('');
		network_card.setButtonVisibility();
	};
	
	// Skip staged rendering when filtering (page already loaded)
	if (skip_staged_render || load.all()) {
		populate_results_callback();
		return true;
	}
	
	// If page is still loading, only display the first few results, as well as
	// the "no results" item. This prevents performing an expensive DOM
	// operation by adding too many results at once while the map is still
	// loading, which can cause a noticeable visual delay. (I know it seems like
	// setting the innerHTML should be fast, but it's not.)
	$results_container.innerHTML = list_items.slice(0, -1).slice(0, 5).join('')
		+ list_items.slice(-1).join('');
	network_card.setButtonVisibility();
	
	// Even though we're waiting until the page has finished loading, it is still
	// necessary to introduce a delay, otherwise displaying the background images
	// of the Google map is further delayed, inexplicably. The delay time is
	// arbitrary, but long enough to allow the map background to load.
	load_callbacks.push(() => {
		setTimeout(() => {
			populate_results_callback();
		}, 800);
	});
}


/**
 * Find scroll position of result
 * @param {element} $result
 * @return {int}
 */
function find_result_scroll_position($result) {
	// const how_much_has_container_scrolled = $scrolling_container.scrollTop;
	const how_much_should_container_scroll = $result.offsetTop;
	return Math.round(how_much_should_container_scroll);
}


/**
 * Show results list panel
 */
function show_results_panel() {
	$scrolling_container.setAttribute(g.is_active_attr, g.true_s);
	if (!is_first_search) return false;
	
	setTimeout(() => {
		is_first_search = false;
	}, results_panel_animation_time + 50);
}


/**
 * Scroll to position
 * @param {int} target_position
 */
function scroll_to_result(target_position) {
	$scrolling_container.scrollTo({
		top: target_position,
		left: 0,
		behavior: 'smooth',
	});
}


/**
 * Get siblings of an element
 * @param {element} $element
 * @return {array}
 */
function get_siblings($element) {
	const $all_children = [...$element.parentNode.children];
	return $all_children.filter(child => child !== $element);
}


/**
 * Clean result name
 * @param {string} input
 * @return {string}
 */
function clean_name(input) {
	return input.replace(/\//g, ' / ').replace(/\s{2,}/g, ' ');
}


/**
 * Round coordinate
 * @param {float} coordinate
 * @param {int} decimals
 * @return {float}
 */
function round_coordinate(coordinate, decimals = 4) {
	const factor = Math.pow(10, decimals);
	return Math.round(coordinate * factor) / factor;
}


function update_contact_form(email_address) {
	const $modal = document.querySelector(`#${contact_modal_id}`);
	const $recipient_container = document.querySelector(contact_modal_recipient_container_$);
	if (!$modal || !$recipient_container) return false;
	
	const $recipient_field = $recipient_container.querySelector(contact_modal_recipient_field_$);
	if (!$recipient_field) return false;
	
	let $recipient_display = $recipient_container.querySelector(`.${contact_modal_recipient_display_c}`);
	if (!$recipient_display) {
		$recipient_display = document.createElement(`span`);
		$recipient_display.className = contact_modal_recipient_display_c;
		$recipient_container.appendChild($recipient_display);
	}
	
	$recipient_field.value = email_address;
	$recipient_display.innerText = email_address;
	
	// A11y: Remove tabindex attributes that disrupt natural tab order
	sanitize_modal_form($modal);
}


/**
 * A11y: Remove tabindex attributes from form fields to restore natural tab order
 * @param {element} $modal
 */
function sanitize_modal_form($modal) {
	if (!$modal) return;
	
	$modal.querySelectorAll('[tabindex]').forEach($el => {
		const tabindex = $el.getAttribute('tabindex');
		// Keep tabindex="-1" for focus trap elements, remove others
		if (tabindex !== '-1') {
			$el.removeAttribute('tabindex');
		}
	});
}


/**
 * Track search input
 * @param {string} input
 */
function track_search(input) {
	clearTimeout(track_search_timeout);
	
	track_search_timeout = setTimeout(() => {
		const current_keyword = input.trim();
		if (current_keyword === previous_tracked_keyword) return false;
		
		previous_tracked_keyword = current_keyword;
		window.dataLayer.push({
			event: 'search_input',
			eventModel: {
				search: current_keyword,
			},
		});
	}, track_search_delay);
}


/**
 * Track click on contact button
 * @param {element} $contact_button
 * @param {string} platform
 */
function track_contact($contact_button, platform) {
	const $result = $contact_button.closest(`[${result_id_attr}]`);
	const group_name = $result.querySelector(`.${result_title_c}`).innerText.trim();
	window.dataLayer.push({
		event: 'group_contact_click',
		eventModel: {
			groupName: group_name,
			elementText: platform,
		},
	});
}


/**
 * Does this key code represent a special key?
 * @param {event} e
 * @param {string} specific_key
 * @return {boolean}
 */
function is_special_key(e, specific_key = null) {
	const special_keys = {
		left: 'ArrowLeft',
		right: 'ArrowRight',
		up: 'ArrowUp',
		down: 'ArrowDown',
	};
	
	if (
		specific_key !== null
		&& Object.keys(special_keys).indexOf(specific_key) !== -1
	) {
		return key_code === special_keys[specific_key];
	}
	
	const key_name = e.key !== undefined ? e.key.toLowerCase() : '';
	const has_shift = e.shiftKey || key_name === 'shift';
	const has_control = e.ctrlKey || key_name === 'control';
	const has_alt = e.altKey || key_name === 'alt';
	const has_command = e.metaKey || key_name === 'meta';
	
	return (
		Object.values(special_keys).indexOf(key_code) !== -1
		|| has_shift
		|| has_control
		|| has_alt
		|| has_command
	);
}


/**
 * Get time elapsed since last timestamp
 * @return {int}
 */
function elapsed_time() {
	const current_time = Date.now();
	const elapsed = current_time - last_time;
	last_time = current_time;
	return elapsed;
}
